import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import {
  asapScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  mergeMap,
  Observable,
  observeOn,
  of,
  ReplaySubject,
  skip,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { defaultHighlightConfig } from 'app/shared/constants/highlight.constant';
import {
  geomFieldsIgnored,
  systemFieldTypesIgnored,
} from 'app/shared/constants/ignore-fields.constant';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { LoadOptionsParams } from 'app/shared/helpers/query-params/load-options-params';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { ExportService } from 'app/shared/services/export.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { ToastService } from 'app/shared/services/toast.service';
import { TranslationService } from 'app/shared/services/translation.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { TabItemLike } from '../../../tabs/tabs.component';
import {
  BufferConfigGroupLike,
  BufferService,
  defaultBufferConfig,
} from '../../buffer.service';
import { HighlightService } from '../../highlight.service';
import { MainMapService } from '../../main-map.service';
import { TocService } from '../../toc.service';
import { HighlightConfigGroupLike } from '../highlight-config-form/highlight-config-form.component';
import { SelectionsChangesLike } from './components/results-table-view/results-table-view.component';
import { SearchResultsInternalService } from './search-results-internal.service';
import moment from 'moment';

type QueryableLayerType = __esri.FeatureLayer | __esri.Sublayer;

interface FieldFilterItemLike {
  field?: __esri.Field;
  list?: CodedValueItem<any>[];
  values?: CodedValueItem<any>[];
  valuesText?: string;
  searchFilterTextSubject: Subject<string>;
  searchFilterTextObservable: Observable<string>;
  includeNull?: boolean;
}

interface SearchResultsHolderLike {
  layer: QueryableLayerType;
  fields: __esri.Field[];
  features: __esri.Graphic[];
  featuresSelected?: __esri.Graphic[];
}

@Component({
  selector: 'app-search-results-internal',
  templateUrl: 'search-results-internal.component.html',
  styleUrls: ['search-results-internal.component.scss'],
  providers: [SearchResultsInternalService],
})
export class SearchResultsInternalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  mapView: __esri.MapView;

  @Input()
  geometryToSearch: __esri.Geometry;

  layerSelected: QueryableLayerType;
  private layerList: Array<QueryableLayerType>;
  private layerListSubject = new ReplaySubject<Array<QueryableLayerType>>(1);
  layerListObservable: Observable<
    Array<{ pathStr: string; layer: QueryableLayerType }>
  > = this.layerListSubject.asObservable().pipe(
    switchMap((layerList) => {
      if (!layerList?.length) {
        return of([]);
      }

      return combineLatest(
        layerList.map((layer) => {
          return EsriUtils.isRecursiveLayerVisibleObservable(layer).pipe(
            map((visible) => {
              if (!visible) {
                return null;
              }

              const pathStr = this.tocService.getPathLayersByLayerAsText(layer);
              return { layer, pathStr };
            })
          );
        })
      ).pipe(
        map((items) => {
          return items.filter((x) => !!x);
        })
      );
    })
  );

  fieldsFromLayerSelected: Array<__esri.Field>;

  filters: FieldFilterItemLike[] = [];

  tabIndexSelected: number;
  get tabSelected() {
    return this.tabs?.[this.tabIndexSelected] ?? null;
  }

  tabs: TabItemLike<SearchResultsHolderLike>[];

  loadingController = new LoadingController();

  initialSearchWithGeometryMode = false;

  get hasGraphicsSelected() {
    return !!this.tabSelected?.data?.featuresSelected?.length;
  }

  get graphicListSelected() {
    return this.tabSelected?.data?.featuresSelected ?? [];
  }

  get isBtnExportDisabled() {
    return !this.hasGraphicsSelected;
  }

  get isBtnExportXlsxVisible() {
    return !this.appConfig.externalMode;
  }

  get isBtnExportKmlVisible() {
    return !this.appConfig.externalMode;
  }

  get isBtnExportShpVisible() {
    return !this.appConfig.externalMode;
  }

  get isBtnFilterDisabled() {
    return (
      !this.layerSelected ||
      !this.filters?.some((x) => x.field && (x.values?.length || x.includeNull))
    );
  }

  get isBtnBufferDisabled() {
    return !this.hasGraphicsSelected;
  }

  searchFilterValueListExpr = (
    item: CodedValueItem<any>,
    filterValue: string
  ) => {
    if (filterValue) {
      return item.label?.toLowerCase()?.includes(filterValue.toLowerCase());
    }

    return true;
  };

  private filterOnTableSubject = new ReplaySubject<void>(1);

  private highlightTabSelectionsSubject = new Subject<{
    tab: TabItemLike<SearchResultsHolderLike>;
    changes: SelectionsChangesLike;
  }>();

  highlightConfigModalVisible = false;
  highlightConfigGroup: HighlightConfigGroupLike = defaultHighlightConfig;
  private highlightConfigSubject = new Subject<void>();

  bufferConfigGroup: BufferConfigGroupLike = defaultBufferConfig;
  private bufferConfigSubject = new Subject<void>();

  bufferFormModalVisible: boolean;

  @HostBinding('class.mobile-mode')
  isMobileScreen: boolean;

  private resultsContentVisibleInternal = false;

  get filterListContentVisible() {
    return !this.isMobileScreen || !this.resultsContentVisibleInternal;
  }

  get resultsContentVisible() {
    return !this.isMobileScreen || this.resultsContentVisibleInternal;
  }

  private mapViewSubscription: Subscription;

  private lifecycle = new Subject<void>();

  constructor(
    private searchResultsService: SearchResultsInternalService,
    private mainMapService: MainMapService,
    private tocService: TocService,
    private highlightService: HighlightService,
    private bufferService: BufferService,
    private exportService: ExportService,
    private appConfig: AppConfigService,
    private toastService: ToastService,
    private globalLoadingController: LoadingController,
    private screenService: ScreenService,
    public translationService: TranslationService
  ) {}

  get layerText() {
    return this.translationService?.getCurrentLanguage()?.['layer'] || 'Layer';
  }

  get fieldText() {
    return this.translationService?.getCurrentLanguage()?.['field'] || 'Field';
  }

  get valuesText() {
    return (
      this.translationService?.getCurrentLanguage()?.['values'] || 'Values'
    );
  }

  get selectValuesPlaceholder() {
    return (
      this.translationService?.getCurrentLanguage()?.[
        'select-values-placeholder'
      ] || 'Select values...'
    );
  }

  get bufferText() {
    return (
      this.translationService?.getCurrentLanguage()?.['buffer'] || 'Buffer'
    );
  }

  get symbologyText() {
    return (
      this.translationService?.getCurrentLanguage()?.['symbology'] ||
      'Symbology'
    );
  }

  get zoomText() {
    return this.translationService?.getCurrentLanguage()?.['zoom'] || 'Zoom';
  }

  get clearText() {
    return this.translationService?.getCurrentLanguage()?.['clear'] || 'Clear';
  }

  get applyText() {
    return this.translationService?.getCurrentLanguage()?.['apply'] || 'Apply';
  }

  get searchPlaceholder() {
    return (
      this.translationService?.getCurrentLanguage()?.['search-placeholder'] ||
      'Search...'
    );
  }

  ngOnInit(): void {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.reset();

        this.updateLayerListCombo(this.layerList);
      });

    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileScreen = isMobile;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.highlightGroupLayer) {
      this.handleMapView();
    }

    if (changes.geometryToSearch) {
      this.initialSearchWithGeometryMode = !!this.geometryToSearch;

      this.filterOnTableSubject.next();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();
  }

  onBtnGoBackClick() {
    this.resultsContentVisibleInternal = false;
  }

  onHighlightConfigChanged() {
    this.highlightConfigSubject.next();
  }

  onLayerSelected() {
    this.filters = [this.initialFilter()];
    this.fieldsFromLayerSelected = this.layerSelected?.fields?.filter(
      (x) =>
        !geomFieldsIgnored.includes(x.name) &&
        !systemFieldTypesIgnored.includes(x.type)
    );
  }

  onFieldFilterChanged(item: FieldFilterItemLike) {
    if (this.globalLoadingController.isShown()) {
      return;
    }

    item.values = [];
    item.valuesText = '';

    const index = this.filters.indexOf(item);
    this.filters.splice(index + 1);

    if (!item.field) {
      item.list = [];
      return;
    }

    const loadOptions = new LoadOptionsParams();

    for (let i = index - 1; i >= 0; --i) {
      const filterItem = this.filters[i];

      if (filterItem.field.type === 'date') {
        if (!filterItem.values?.[0]?.value) {
          delete filterItem.values[0];
        }

        if (!filterItem.values?.[1]?.value) {
          delete filterItem.values[1];
        }

        if (filterItem.values?.[1]) {
          const day = filterItem.values?.[1].value.getDate();
          filterItem.values?.[1].value.setDate(day + 1);
          filterItem.values?.[1].value.setUTCHours(0, 0, 0, 0);
        }

        if (filterItem.values?.[0]) {
          filterItem.values?.[0].value.setUTCHours(0, 0, 0, 0);
        }

        if (filterItem.values[0] && filterItem.values[1]) {
          loadOptions.appendFilterBetween(
            filterItem.field.name,
            filterItem.values.map((x) => x.value)[0],
            filterItem.values.map((x) => x.value)[1]
          );
        }

        if (!filterItem.values[0] && filterItem.values[1]) {
          loadOptions.appendFilterMaxDate(
            filterItem.field.name,
            filterItem.values.map((x) => x.value)[1]
          );
        }

        if (filterItem.values[0] && !filterItem.values[1]) {
          loadOptions.appendFilterMinDate(
            filterItem.field.name,
            filterItem.values.map((x) => x.value)[0]
          );
        }
      } else if (
        filterItem.field.type === 'integer' ||
        filterItem.field.type === 'double' ||
        filterItem.field.type === 'long'
      ) {
        if (
          !filterItem.values?.[0]?.value ||
          isNaN(filterItem.values?.[0]?.value)
        ) {
          delete filterItem.values[0];
        }

        if (
          !filterItem.values?.[1]?.value ||
          isNaN(filterItem.values?.[1]?.value)
        ) {
          delete filterItem.values[1];
        }

        if (filterItem.values[0] && filterItem.values[1]) {
          loadOptions.appendFilterBetween(
            filterItem.field.name,
            filterItem.values.map((x) => parseFloat(x.value))[0],
            filterItem.values.map((x) => parseFloat(x.value))[1]
          );
        }

        if (!filterItem.values[0] && filterItem.values[1]) {
          loadOptions.appendFilterMaxDate(
            filterItem.field.name,
            filterItem.values.map((x) => parseFloat(x.value))[1]
          );
        }

        if (filterItem.values[0] && !filterItem.values[1]) {
          loadOptions.appendFilterMinDate(
            filterItem.field.name,
            filterItem.values.map((x) => parseFloat(x.value))[0]
          );
        }
      } else {
        loadOptions.appendFilter([
          filterItem.field.name,
          'in',
          filterItem.values.map((x) => x.value),
        ]);
      }
    }

    const whereStr = loadOptions.toWhereClauseStr();

    this.globalLoadingController.show();
    this.searchResultsService
      .getDistinticValues(this.layerSelected, item.field.name, whereStr)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (list) => {
          item.list = list.filter((x) => x.value);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-load-data']
          );
        },
      });
  }

  onNumberFieldValueFilterSelectChanged(
    item: FieldFilterItemLike,
    number: number,
    arrPosition: number
  ) {
    const value = { value: `${number}`, label: `${number}` };

    const index = this.filters.indexOf(item);
    this.filters.splice(index + 1);

    if (!item.values) {
      item.values = [];
    }

    if (!item.values?.includes(value)) {
      item.values[arrPosition] = value;
    } else {
      ArrayUtils.removeItem(item.values, value);
    }

    item.valuesText = item.values.map((x) => x.label).join(', ');
  }

  onDateFieldValueFilterSelectChanged(
    item: FieldFilterItemLike,
    date: Date,
    arrPosition: number
  ) {
    const value = { value: date, label: moment(date).format('MM-DD-YYYY') };

    const index = this.filters.indexOf(item);
    this.filters.splice(index + 1);

    if (!item.values) {
      item.values = [];
    }

    if (!item.values?.includes(value)) {
      item.values[arrPosition] = value;
    } else {
      ArrayUtils.removeItem(item.values, value);
    }

    item.valuesText = item.values.map((x) => x.label).join(', ');
  }

  onIncludeNullChange(includeNull: boolean, item: FieldFilterItemLike) {
    item.includeNull = includeNull;
  }

  onFieldValueFilterSelectChanged(
    item: FieldFilterItemLike,
    value: CodedValueItem<any>,
    selected: boolean
  ) {
    const index = this.filters.indexOf(item);
    this.filters.splice(index + 1);

    if (!item.values) {
      item.values = [];
    }

    if (selected) {
      item.values.push(value);
    } else {
      ArrayUtils.removeItem(item.values, value);
    }

    item.valuesText = item.values.map((x) => x.label).join(', ');
  }

  onBtnDeleteFilterClick(filterIndex: number) {
    this.filters?.splice(filterIndex);
  }

  onBtnAddFilterClick() {
    this.filters.push(this.initialFilter());
  }

  onBtnApplyClick() {
    this.filterOnTableSubject.next();
  }

  onBtnClearClick() {
    this.reset();
  }

  onBtnZoomToClick() {
    if (!this.hasGraphicsSelected) {
      return;
    }

    const geometryList = this.graphicListSelected.map((x) => x.geometry);
    this.mainMapService.goTo(geometryList);
  }

  onBtnHighlightConfigClick() {
    this.highlightConfigModalVisible = true;
  }

  onBtnExportXlsxClick() {
    this.exportData((fields, features) => {
      features = features.map(({ attributes }) => {
        return { attributes };
      }) as any;
      return this.exportService.exportAsXlsxLink({
        fields,
        features,
      });
    });
  }

  onBtnExportKmlClick() {
    this.exportData(
      (fields, features) =>
        this.exportService.exportAsKmlLink({
          fields,
          features,
        }),
      this.appConfig.exportLimit
    );
  }

  onBtnExportShpClick() {
    this.exportData(
      (fields, features) =>
        this.exportService.exportAsShpLink({
          fields,
          features,
        }),
      this.appConfig.exportLimit
    );
  }

  onBtnBufferClick() {
    this.bufferFormModalVisible = true;
  }

  onBufferParamsApplyClick() {
    this.bufferConfigSubject.next();
  }

  onFeaturesSelectedChanged(
    tab: TabItemLike<SearchResultsHolderLike>,
    changes: SelectionsChangesLike
  ) {
    this.highlightTabSelectionsSubject.next({ tab, changes });
  }

  isFieldValueFilterSelected(
    item: FieldFilterItemLike,
    value: CodedValueItem<any>
  ) {
    return !!item.values?.includes(value);
  }

  canDeleteFilterItem() {
    return this.filters?.length > 1;
  }

  reset() {
    this.tabs = [];
    this.layerSelected = null;
    this.filters = null;
    this.resultsContentVisibleInternal = false;
    this.filterOnTableSubject.next();
    this.bufferConfigSubject.next();
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    const allLayers = this.tocService
      .getLayerMenuItems()
      .flatMap((menuItem) => {
        return EsriUtils.flatLayers(this.tocService.getLayersByItem(menuItem))
          .filter(
            (layer) =>
              (EsriUtils.isQueryableLayer(layer) &&
                !EsriUtils.isSubLayer(layer)) ||
              layer instanceof MapImageLayer
          )
          .flatMap<QueryableLayerType>((layer) => {
            const list = EsriUtils.getDeeperLayers([layer as any]);
            if (list) {
              return list;
            } else {
              return [layer] as any;
            }
          })
          .filter((layer) => {
            return (
              (layer as __esri.FeatureLayer).capabilities?.operations
                ?.supportsQuery !== false &&
              EsriUtils.isLoaded(layer) &&
              EsriUtils.isQueryableLayer(layer)
            );
          })
          .reverse();
      });

    this.layerList = allLayers;
    this.updateLayerListCombo(this.layerList);

    subscription.add(this.getFilterOnTableObservable(allLayers).subscribe());

    subscription.add(() => {
      this.layerList = null;
      this.updateLayerListCombo(this.layerList);
    });

    this.mapViewSubscription = subscription;
  }

  private getFilterOnTableObservable(layerList: QueryableLayerType[]) {
    return this.filterOnTableSubject.pipe(
      observeOn(asapScheduler),
      switchMap(() => {
        const geometry = this.geometryToSearch;
        if (!geometry && this.isBtnFilterDisabled) {
          return EMPTY;
        }

        this.resultsContentVisibleInternal = true;

        const { layerSelected, initialSearchWithGeometryMode } = this;

        const loadingController = initialSearchWithGeometryMode
          ? this.globalLoadingController
          : this.loadingController;

        const whereStr = this.getWhereClauseStr();

        let layersToSearch: QueryableLayerType[];
        if (layerSelected && !initialSearchWithGeometryMode) {
          layersToSearch = [layerSelected];
        } else {
          layersToSearch = layerList.filter((layer) =>
            EsriUtils.isRecursiveLayerVisible(layer)
          );
        }

        const searchOnLayerObservableList = layersToSearch.map((layer) => {
          const query = layer.createQuery();
          query.outFields = ['*'];
          query.where = whereStr;
          query.geometry = geometry;
          query.returnGeometry = true;

          return EsriUtils.queryFeatures(layer, query).pipe(
            map((featureSet) => {
              const attributesWithDomain = layer.fields.filter((x) => x.domain);
              EsriUtils.handleFieldDomains(
                featureSet.features,
                attributesWithDomain
              );

              return { layer, featureSet };
            }),
            observeOn(asapScheduler)
          );
        });

        loadingController.show();
        return combineLatest(searchOnLayerObservableList).pipe(
          catchError((response) => {
            if (response) {
              this.toastService.error(
                this.translationService.getCurrentLanguage()['failed-load-data']
              );
            }

            return EMPTY;
          }),
          defaultIfEmpty(
            [] as {
              layer: QueryableLayerType;
              featureSet: __esri.FeatureSet;
            }[]
          ),
          finalize(() => {
            loadingController.hide();
          }),
          mergeMap((res) => {
            if (geometry && !layerSelected) {
              res = res.filter((x) => x.featureSet.features.length);
              const layersAux = res.map((x) => x.layer);
              this.updateLayerListCombo(layersAux);
            }

            const newTabs = res.map<TabItemLike<SearchResultsHolderLike>>(
              ({ layer, featureSet }) => {
                const fieldsTable = (layer.fields ?? featureSet.fields).filter(
                  (x) =>
                    !geomFieldsIgnored.includes(x.name) &&
                    !systemFieldTypesIgnored.includes(x.type)
                );
                const features = featureSet.features;

                const holder: SearchResultsHolderLike = {
                  layer,
                  fields: fieldsTable,
                  features,
                };

                return {
                  text: layer.title,
                  data: holder,
                };
              }
            );

            if (
              !this.tabs?.length ||
              !geometry ||
              initialSearchWithGeometryMode
            ) {
              this.tabs = [];
            }

            const predicateTabFn = (
              layer: QueryableLayerType,
              tab: TabItemLike<SearchResultsHolderLike>
            ) => {
              return layer === tab.data.layer;
            };
            this.tabs = layerList
              .map(
                (layer) =>
                  newTabs.find((tab) => predicateTabFn(layer, tab)) ??
                  this.tabs.find((tab) => predicateTabFn(layer, tab))
              )
              .filter((x) => !!x);

            this.tabIndexSelected = !this.tabs.length
              ? -1
              : !layerSelected
              ? 0
              : this.tabs.findIndex((x) => x.data.layer === layerSelected);

            this.initialSearchWithGeometryMode = false;

            return new Observable((subscriber) => {
              subscriber.add(
                this.getHighlightObservable(this.tabs).subscribe()
              );
              subscriber.add(this.getBufferObservable(this.tabs).subscribe());
            });
          })
        );
      })
    );
  }

  private getHighlightObservable(
    tabs: TabItemLike<SearchResultsHolderLike>[]
  ): Observable<any> {
    return new Observable((subscriber) => {
      tabs?.forEach((tab) => {
        const holder = tab.data;
        holder.featuresSelected = [];

        if (!holder.features?.length) {
          return;
        }

        const subscription = new Subscription();

        const highlightSubscriptionMap = new Map<
          __esri.Graphic,
          Subscription
        >();

        subscription.add(
          merge(
            this.highlightTabSelectionsSubject.pipe(
              filter((x) => x?.tab === tab),
              map((x) => x.changes)
            ),
            this.highlightConfigSubject.pipe(
              map(() => {
                const graphics = Array.from(highlightSubscriptionMap.keys());
                return { adds: graphics, deletes: graphics };
              })
            )
          ).subscribe((changes) => {
            changes.deletes?.forEach((graphic) => {
              highlightSubscriptionMap.get(graphic)?.unsubscribe();
              highlightSubscriptionMap.delete(graphic);
            });

            changes.adds?.forEach((graphic) => {
              const highlightSubscription = this.highlightService
                .highlight(graphic.geometry, this.highlightConfigGroup)
                .subscribe();
              highlightSubscriptionMap.set(graphic, highlightSubscription);
            });
          })
        );

        subscription.add(() => {
          for (const x of highlightSubscriptionMap.values()) {
            x.unsubscribe();
          }

          highlightSubscriptionMap.clear();
        });

        subscriber.add(subscription);
      });
    });
  }

  private getBufferObservable(
    tabs: TabItemLike<SearchResultsHolderLike>[]
  ): Observable<any> {
    return new Observable((subscriber) => {
      tabs?.forEach((tab) => {
        const subscription = new Subscription();

        const bufferSubscriptionMap = new Map<__esri.Graphic, Subscription>();

        subscription.add(
          merge(
            this.bufferConfigSubject.pipe(
              first(),
              map(() => {
                const featuresSelected = tab.data.featuresSelected;
                return { adds: featuresSelected };
              })
            ),
            this.bufferConfigSubject.pipe(
              skip(1),
              map(() => {
                const graphics = Array.from(bufferSubscriptionMap.keys());
                return { adds: graphics, deletes: graphics };
              })
            )
          )
            .pipe(
              switchMap((changes) => {
                return merge(
                  of(changes),
                  this.highlightTabSelectionsSubject.pipe(
                    filter((x) => x.tab == tab),
                    map((x) => x.changes)
                  )
                );
              })
            )
            .subscribe((changes: SelectionsChangesLike) => {
              changes.deletes?.forEach((graphic) => {
                bufferSubscriptionMap.get(graphic)?.unsubscribe();
                bufferSubscriptionMap.delete(graphic);
              });

              changes.adds?.forEach((graphic) => {
                const bufferSubscription = this.bufferService
                  .buffer(graphic.geometry, this.bufferConfigGroup)
                  .subscribe();
                bufferSubscriptionMap.set(graphic, bufferSubscription);
              });
            })
        );

        subscription.add(() => {
          for (const x of bufferSubscriptionMap.values()) {
            x.unsubscribe();
          }

          bufferSubscriptionMap.clear();
        });

        subscriber.add(subscription);
      });
    });
  }

  private getWhereClauseStr() {
    const loadOptions = new LoadOptionsParams();

    const filters =
      this.filters?.filter(
        (x) => !!(x.field && (x.values?.length || x.includeNull))
      ) ?? [];

    for (const item of filters) {
      if (item.values?.length) {
        if (item.field.type === 'date') {
          if (!item.values?.[0]?.value) {
            delete item.values[0];
          }

          if (!item.values?.[1]?.value) {
            delete item.values[1];
          }

          if (item.values?.[1]) {
            const day = item.values?.[1].value.getDate();
            item.values?.[1].value.setDate(day + 1);
            item.values?.[1].value.setUTCHours(0, 0, 0, 0);
          }

          if (item.values?.[0]) {
            item.values?.[0].value.setUTCHours(0, 0, 0, 0);
          }

          if (item.values[0] && item.values[1]) {
            loadOptions.appendFilterBetween(
              item.field.name,
              item.values.map((x) => x.value)[0],
              item.values.map((x) => x.value)[1]
            );
          }

          if (!item.values[0] && item.values[1]) {
            loadOptions.appendFilterMaxDate(
              item.field.name,
              item.values.map((x) => x.value)[1]
            );
          }

          if (item.values[0] && !item.values[1]) {
            loadOptions.appendFilterMinDate(
              item.field.name,
              item.values.map((x) => x.value)[0]
            );
          }
        } else if (
          item.field.type === 'integer' ||
          item.field.type === 'double' ||
          item.field.type === 'long'
        ) {
          if (!item.values?.[0]?.value || isNaN(item.values?.[0]?.value)) {
            delete item.values[0];
          }

          if (!item.values?.[1]?.value || isNaN(item.values?.[1]?.value)) {
            delete item.values[1];
          }

          if (item.values[0] && item.values[1]) {
            loadOptions.appendFilterBetween(
              item.field.name,
              item.values.map((x) => parseFloat(x.value))[0],
              item.values.map((x) => parseFloat(x.value))[1]
            );
          }

          if (!item.values[0] && item.values[1]) {
            loadOptions.appendFilterMaxDate(
              item.field.name,
              item.values.map((x) => parseFloat(x.value))[1]
            );
          }

          if (item.values[0] && !item.values[1]) {
            loadOptions.appendFilterMinDate(
              item.field.name,
              item.values.map((x) => parseFloat(x.value))[0]
            );
          }
        } else {
          loadOptions.appendFilter([
            item.field.name,
            'in',
            item.values.map((x) => x.value),
          ]);
        }
      }

      if (item.includeNull) {
        loadOptions.appendFilter([item.field.name, 'is', null], 'or');
      }
    }

    return loadOptions.toWhereClauseStr();
  }

  getMinorValue(values: { label: string; value: Date | number }[] | undefined) {
    if (!values) {
      return 'Min:';
    }

    const validValues = values?.filter((v) => v.value !== null);

    if (validValues.length === 0) {
      return 'Min:';
    }

    const minor = validValues.reduce((min, item) =>
      item.value! < min.value! ? item : min
    );

    const text = `Min: ${minor.label.split(' ')[0].replace('.', ',')}`;

    return text;
  }

  getMaxValue(values: { label: string; value: Date | number }[] | undefined) {
    if (!values) {
      return 'Max:';
    }

    const validValues = values.filter((v) => v.value !== null);

    if (validValues.length === 0) {
      return 'Max:';
    }

    const major = validValues.reduce((max, item) =>
      item.value! > max.value! ? item : max
    );

    const text = `Max: ${major.label.split(' ')[0].replace('.', ',')}`;

    return text;
  }

  private initialFilter() {
    const searchFilterTextSubject = new BehaviorSubject<string>('');
    const searchFilterTextObservable = searchFilterTextSubject.pipe(
      debounceTime(300),
      observeOn(asapScheduler)
    );

    return { searchFilterTextSubject, searchFilterTextObservable };
  }

  private updateLayerListCombo(layerList: QueryableLayerType[]) {
    this.layerListSubject.next(layerList);
  }

  private exportData(
    fn: (
      fields: __esri.Field[],
      features: __esri.Graphic[]
    ) => Observable<string>,
    limit?: number
  ) {
    if (this.globalLoadingController.isShown() || this.isBtnExportDisabled) {
      return;
    }

    const tabItem = this.tabSelected;
    let features = this.graphicListSelected;

    if (typeof limit === 'number' && features.length > limit) {
      features = features.slice(0, limit);

      this.toastService.info(
        this.translationService.getCurrentLanguage()['export-large-items']
      );
    }

    this.globalLoadingController.show();
    fn(tabItem.data.fields, features)
      .pipe(
        finalize(() => {
          this.globalLoadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: (link) => {
          window.open(link, '_blank');
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.translationService.getCurrentLanguage()['failed-export-data']
          );
        },
      });
  }
}
